import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '../index'

export interface UploadFailure {
    message: string
    isRetryable: boolean
}

export interface UploadProgressEntry {
    progress: number
    failure: UploadFailure | null
}

export interface UploadProgressState {
    entries: Record<string, UploadProgressEntry>
}

const initialState: UploadProgressState = {
    entries: {},
}

export const uploadProgressSlice = createSlice({
    name: 'uploadProgress',
    initialState,
    reducers: {
        setUploadProgress(
            state,
            action: PayloadAction<{ consultId: string; progress: number }>
        ) {
            const { consultId, progress } = action.payload
            const clamped = Math.min(progress, 1.0)
            const existing = state.entries[consultId]

            // Monotonic invariant: reject if new value is less than current
            if (existing && clamped < existing.progress) {
                return
            }

            state.entries[consultId] = {
                progress: clamped,
                failure: existing?.failure ?? null,
            }
        },

        setUploadFailure(
            state,
            action: PayloadAction<{
                consultId: string
                message: string
                isRetryable: boolean
            }>
        ) {
            const { consultId, message, isRetryable } = action.payload
            state.entries[consultId] = {
                progress: 0,
                failure: { message, isRetryable },
            }
        },

        clearUploadProgress(state, action: PayloadAction<string>) {
            delete state.entries[action.payload]
        },

        clearUploadFailure(state, action: PayloadAction<string>) {
            const entry = state.entries[action.payload]
            if (entry) {
                entry.failure = null
            }
        },
    },
})

export const {
    setUploadProgress,
    setUploadFailure,
    clearUploadProgress,
    clearUploadFailure,
} = uploadProgressSlice.actions

export const uploadProgressReducer = uploadProgressSlice.reducer

// Selectors
export const selectUploadProgress = (
    state: RootState,
    consultId: string
): number | undefined => state.uploadProgress.entries[consultId]?.progress

export const selectUploadFailure = (
    state: RootState,
    consultId: string
): UploadFailure | undefined =>
    state.uploadProgress.entries[consultId]?.failure ?? undefined
